import { buildCanonical, resolvePageSeoMetadata, type AlternateLink, type PageSeoMetadata } from './routes'

export interface BlogSeoPost {
  slug: string
  locale: string
  title: string
  description: string
  ogImage?: string
  date: string
  updated?: string
  translations: Readonly<Record<string, string | undefined>>
  faq: readonly { q: string; a: string }[]
}

export interface BlogSeoOptions {
  domain: string
  brandName: string
  defaultLocale?: string
  defaultOgImage?: string
  titleSeparator?: string
}

/**
 * The single blog metadata projection used by Node SSG and the hydrated page.
 * It intentionally owns canonical, hreflang and every Article schema together.
 */
export function buildBlogPageMetadata(
  post: BlogSeoPost,
  posts: readonly BlogSeoPost[],
  options: BlogSeoOptions,
): PageSeoMetadata {
  const path = `/blog/${post.slug}`
  const canonical = buildCanonical(options.domain, path)
  const alternates = buildAlternates(post, posts, options)

  return resolvePageSeoMetadata(options.domain, {
    path,
    title: `${post.title}${options.titleSeparator ?? ' - '}${options.brandName}`,
    description: post.description,
    ogImage: post.ogImage,
    lastmod: post.updated ?? post.date,
    changefreq: 'monthly',
    priority: 0.5,
  }, {
    locale: post.locale || options.defaultLocale || 'ru',
    defaultImage: options.defaultOgImage,
    alternates,
    alternatesNoneReason: 'This article has no translated source pair.',
    openGraphType: 'article',
    schema: {
      mode: 'required',
      schemas: [
        {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: post.title,
          description: post.description,
          datePublished: post.date,
          ...(post.updated ? { dateModified: post.updated } : {}),
          inLanguage: post.locale,
          url: canonical,
          mainEntityOfPage: canonical,
          author: { '@type': 'Organization', name: options.brandName },
          publisher: { '@type': 'Organization', name: options.brandName },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: options.brandName, item: buildCanonical(options.domain, '/') },
            { '@type': 'ListItem', position: 2, name: post.title, item: canonical },
          ],
        },
        ...(post.faq.length > 0 ? [{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: post.faq.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        }] : []),
      ],
    },
  })
}

function buildAlternates(
  post: BlogSeoPost,
  posts: readonly BlogSeoPost[],
  options: BlogSeoOptions,
): AlternateLink[] | undefined {
  const translated = Object.entries(post.translations)
    .filter(([locale, slug]) => locale !== post.locale && Boolean(slug))
  if (translated.length === 0) return undefined

  const bySlug = new Map(posts.map((item) => [item.slug, item]))
  const slugByLocale = new Map<string, string>([[post.locale, post.slug]])
  for (const [, slug] of translated) {
    const translatedPost = slug ? bySlug.get(slug) : undefined
    if (translatedPost) slugByLocale.set(translatedPost.locale, translatedPost.slug)
  }

  const links = [...slugByLocale.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([locale, slug]) => ({ hreflang: locale, href: buildCanonical(options.domain, `/blog/${slug}`) }))
  const defaultSlug = slugByLocale.get(options.defaultLocale ?? 'ru') ?? post.slug
  links.push({ hreflang: 'x-default', href: buildCanonical(options.domain, `/blog/${defaultSlug}`) })
  return links
}
